/**
 * Bar chart view component using Recharts.
 * Supports grouped (side-by-side) and stacked bar variants.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ChartConfig, ChartReferenceLine, TransformedChartData } from '../../types/chartTypes';
import { DEFAULT_CHART_COLORS } from '../../types/chartTypes';
import { useXAxisTickLayout } from '../../hooks/useXAxisTickLayout';
import { abbreviateAxisTick, CARTESIAN_MARGIN, formatTooltipValue } from '../../utils/axisFormat';
import { renderChartReferenceLines } from './chartReferenceLines';

type BarChartViewProps = {
  data: TransformedChartData;
  config: ChartConfig;
  height?: number | `${number}%`;
  /** Recharts default is true; dashboard tiles animate only on first mount. */
  isAnimationActive?: boolean;
  /** Target/goal lines (dashboard tiles). */
  referenceLines?: ChartReferenceLine[];
  /** Raw mark-click passthrough (S11) — ChartRenderer normalizes/resolves. */
  onMark?: (...args: unknown[]) => void;
  /** ResponsiveContainer resize debounce (ms) — dashboard tiles pass ~150. */
  resizeDebounceMs?: number;
};

export function BarChartView({ data, config, height = 400, isAnimationActive, referenceLines, onMark, resizeDebounceMs }: BarChartViewProps) {
  const colors = config.colors || DEFAULT_CHART_COLORS;
  const isStacked = config.barVariant === 'stacked';
  const { layout: xTicks, onResize } = useXAxisTickLayout(data.data, data.xDataKey);

  if (data.data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No data to display
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height} debounce={resizeDebounceMs} onResize={onResize}>
      <BarChart data={data.data} margin={CARTESIAN_MARGIN}>
        {config.showGrid && <CartesianGrid strokeDasharray="3 3" />}
        <XAxis
          dataKey={data.xDataKey}
          tick={{ fontSize: 12 }}
          angle={xTicks.angle}
          textAnchor={xTicks.textAnchor}
          height={xTicks.height}
        />
        {/* preserveStartEnd keeps the 0 baseline label when ticks are culled. */}
        <YAxis tick={{ fontSize: 12 }} tickFormatter={abbreviateAxisTick} interval="preserveStartEnd" />
        {renderChartReferenceLines(referenceLines)}
        <Tooltip
          formatter={(value, name) => [formatTooltipValue(value), name]}
          contentStyle={{
            backgroundColor: 'var(--background)',
            border: '1px solid var(--border)',
            borderRadius: '0.375rem',
          }}
        />
        {config.showLegend && <Legend verticalAlign="top" height={36} />}
        {data.seriesKeys.map((key, index) => (
          <Bar
            key={key}
            isAnimationActive={isAnimationActive}
            dataKey={key}
            fill={colors[index % colors.length]}
            name={key}
            stackId={isStacked ? 'stack' : undefined}
            onClick={onMark}
            cursor={onMark ? 'pointer' : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
